// src/components/OrgLicenseSummary.jsx
//
// Renders per-tier seat usage. Receives `data` from the parent
// (OrgAdminPage) which fetches it via /api/v1/org/license.
//
// Data shape: {
//   tiers: [{
//     tier_code, display_name, seats_total, seats_used, seats_unlimited,
//     term_state: "live"|"grace"|"lapsed"|"pending"|"none", days_remaining
//   }]
// }
//
// ── TERM VOCABULARY. 2026-08-31, MR 2. ─────────────────────────────────────
//
// Until this MR a lapsed tier never reached this component: the licence
// endpoint filtered non-current terms out of its purchased CTE, so the row
// vanished upstream and this file only ever saw healthy tiers. Making the
// filter a classification fixed the payload and, on its own, would have left
// this row rendering "7 assigned · unlimited" beside a dead contract —
// technically the same sentence it always rendered, now attached to a licence
// that has stopped.
//
// A row this MR made visible cannot be left mute. `term_state` is rendered
// alongside the count, and a non-live term recolours the row so it does not
// read as healthy at a glance.
//
// The STATE is what is shown, never a recomputed date. term_state arrives
// decided by aiprime.org_holds_tier (migration 061); comparing term_end to a
// browser clock here would be a second implementation of the rule.
//
// Unlimited tiers (e.g., Enterprise Site) skip the progress bar and
// just render "<used> seats assigned" — there's no cap to track against.
//
// The progress-bar color tracks usage on metered tiers:
//   ≥100% red    — over-cap, hard problem
//   ≥80%  yellow — getting close
//   <80%  teal   — healthy
//
// If the org has zero relevant tiers, the whole component renders nothing.

import { LICENSE_TIER_LABEL } from "../constants/licenseTiers";

// Rendered beside the seat count. `live` deliberately has no chip — the
// healthy case should be the quiet one, or every row shouts and none is read.
const TERM_CHIP = {
  grace:   { text: "in grace",         cls: "text-amber-300" },
  lapsed:  { text: "lapsed",           cls: "text-red-400"   },
  pending: { text: "not started",      cls: "text-sky-300"   },
  none:    { text: "no active license", cls: "text-red-400"  },
};

export default function OrgLicenseSummary({ data }) {
  const tiers = data?.tiers ?? [];
  if (!tiers.length) return null;

  const anyOverCap = tiers.some(
    t => !t.seats_unlimited
      && Number(t.seats_total) > 0
      && Number(t.seats_used) >= Number(t.seats_total)
  );

  return (
    <div className="mb-4 rounded border border-bg-border bg-bg-soft p-3 space-y-3">
      <div className="text-sm text-gray-300 font-semibold">Licenses</div>

      {tiers.map((t) => {
        const used = Number(t.seats_used) || 0;
        const total = Number(t.seats_total) || 0;
        // Frontend constants take precedence over backend display_name
        // so labels stay consistent across the app.
        const label = LICENSE_TIER_LABEL[t.tier_code] ?? t.display_name ?? t.tier_code;

        const chip = TERM_CHIP[t.term_state] ?? null;

        if (t.seats_unlimited) {
          return (
            <div key={t.tier_code} className="flex justify-between items-center">
              <span className="text-xs text-gray-400">{label}</span>
              <span className="text-xs font-semibold text-gray-100">
                {used}{" "}
                <span className="text-fg-secondary font-normal">
                  assigned · {chip ? <span className={chip.cls}>{chip.text}</span> : "unlimited"}
                </span>
              </span>
            </div>
          );
        }

        const pct = total > 0 ? (used / total) * 100 : 0;

        return (
          <div key={t.tier_code}>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-400">{label}</span>
              <span className="text-xs font-semibold text-gray-100">
                {used} / {total}
                {chip && <span className={`ml-1.5 font-normal ${chip.cls}`}>{chip.text}</span>}
              </span>
            </div>
            <div className="mt-1 h-1.5 rounded bg-bg">
              <div
                className={`h-1.5 rounded transition-[width] duration-200 ${
                  chip ? "bg-slate-600"          // term not live — usage is not the story
                    : pct >= 100 ? "bg-red-500"
                    : pct >= 80 ? "bg-yellow-500"
                    : "bg-status-ok"
                }`}
                style={{ width: `${Math.min(pct, 100)}%` }}
              />
            </div>
          </div>
        );
      })}

      {anyOverCap && (
        <div className="text-xs text-red-400">
          One or more tiers is full. Deactivate a user or upgrade to add seats.
        </div>
      )}
    </div>
  );
}